import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import { useNavigate } from 'react-router-dom';

// components
import WidgetTitle from '../WidgetTitle';
import AffiliatedRestaurants from '../../../../../ui-kitchen/src/components/AffiliatedRestaurants';
import ClampLines from '../../../../../ui-kitchen/src/components/ClampLines';
import DayOfEventCard from '../../../../../ui-kitchen/src/components/DayOfEventCard';
import DropdownGroup from '../../../../../ui-kitchen/src/components/DropdownGroup';
import Notify from '../../../../../ui-kitchen/src/components/Notify';
import PartnershipBranding from '../../../../../ui-kitchen/src/components/PartnershipBranding';
import PoweredBy from '../../../../../ui-kitchen/src/components/PoweredBy';
import ResyCalendar from '../../../../../ui-kitchen/src/components/ResyCalendar';
import SafetyMeasures from '../../../../../ui-kitchen/src/components/SafetyMeasures';
import SafetyMeasureList from '../../../../../ui-kitchen/src/components/SafetyMeasureList';
import ShiftInventory from '../../../../../ui-kitchen/src/components/ShiftInventory';
import UpcomingEvents from '../../../../../ui-kitchen/src/components/UpcomingEvents';
import VenueAnnouncement from '../../../../../ui-kitchen/src/components/VenueAnnouncement';
import VenueLocationSummary from '../../../../../ui-kitchen/src/components/VenueLocationSummary';
import ReservationShape from '../../../../../ui-kitchen/src/components/types/reservations';
import { GatingMessageModal } from '../../../../../ui-kitchen/src/components/Gating/GatingMessageModal';
import ResyCreditBanner from '../../../../../ui-kitchen/src/components/ResyCreditBanner';
import useExperiment from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { IS_JCE_BRIDGE_ENABLED } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';

const VenueDetailsView = (props) => {
  const {
    Analytics,
    aboutVenue,
    aboutVenueHeader,
    activeNotifications,
    affiliatedRestaurants,
    isCalendarOpen,
    clearTimeFilter,
    clickableElementIdsToIgnore,
    currentMonth,
    dateLabel,
    doubleBookingError,
    eventsExistOnDay,
    expandSafetyMeasures,
    filterQuery,
    findNextAvailable,
    focusTrap,
    getAriaLabel,
    handleBookEvent,
    handleReservationSelect,
    handleTodayEventClick,
    hasAboutVenue,
    hasActiveSafetyCategories,
    hasAffiliatedRestaurants,
    hasNeedToKnow,
    hasPartnershipBranding,
    hasTodayEvents,
    inventoryByDay,
    isResyCreditEligible,
    isVenueClosed,
    largePartyMessage,
    loadReservations,
    isVenueReservationsLoading,
    maxQuantity,
    minQuantity,
    needToKnow,
    needToKnowHeader,
    notifyData,
    openNotify,
    onPartySizeChange,
    onTimeChange,
    platinumNight,
    reservations,
    safetyCategories,
    isSafetyMeasuresExpanded,
    scrollTop,
    selectedDate,
    selectedDateFormatted,
    selectedISODate,
    selectedQuantity,
    selectedTime,
    serviceTypeId,
    setSelectedDate,
    shopSmallValue,
    todayEvents,
    todayEventsCount,
    toggleCalendar,
    upcomingEvents,
    userIsGda,
    venue,
    venueId,
    venueName,
    matchMedia,
    venueInventoryCache,
    timeOptions,
    displayGatingErrorModal,
    ResyAmexConfig,
    closeModal,
  } = props;

  // Angular shims for ui-kitchen components
  const navigate = useNavigate();
  const $routeReloadShim = { reload: () => navigate(0) };
  const $scope$onShim = { $on: () => { } };
  const [isResyCreditBannerEnabled] = useExperiment(IS_JCE_BRIDGE_ENABLED);

  return (
    <div className="widget-venue-page">
      <div className="widget-title">
        <WidgetTitle text={venueName} />
      </div>
      <Notify
        $route={$routeReloadShim}
        $scope={$scope$onShim}
        $window={window}
        Analytics={Analytics}
        activeNotifications={activeNotifications}
        context="widget"
        focusTrap={focusTrap}
        isGda={userIsGda}
        loadReservations={loadReservations}
        openModal={openNotify}
        reservations={reservations}
        scrollTop={scrollTop}
        selectedDate={selectedISODate}
        selectedQuantity={selectedQuantity}
        serviceTypeId={serviceTypeId}
        venueId={venueId}
        venueName={venueName}
      />
      {/* address fb_pixel value in string */}
      <noscript>
        <img height="1" width="1" style={{ display: 'none' }} src={`https://www.facebook.com/tr?id=${venue.fb_pixel}&ev=PageView&noscript=1`} alt="" />
      </noscript>

      {hasNeedToKnow && (
        <div className="about-venue desktop-modal-padding container">
          <h3>{needToKnowHeader}</h3>
          <ClampLines
            text={needToKnow.body || ''}
            id="venue-need-to-know"
            lines={3}
            className="content"
          />
        </div>
      )}
      <div className="desktop-modal-padding container">
        <DropdownGroup
          Analytics={Analytics}
          dateLabel={dateLabel}
          isLargePartyConfigured={!!largePartyMessage}
          isMobile={!matchMedia.sm}
          maxPartySize={maxQuantity}
          minPartySize={minQuantity}
          onPartySizeChange={onPartySizeChange}
          onTimeChange={onTimeChange}
          reservations={reservations}
          selectedQuantity={selectedQuantity}
          selectedTime={selectedTime}
          selectedDateFormatted={selectedDateFormatted}
          toggleCalendar={toggleCalendar}
          venue={venue}
          shopSmallValue={shopSmallValue}
          timeOptions={timeOptions}
        />
        {isCalendarOpen && (
          <ResyCalendar
            Analytics={Analytics}
            calendarCache={venueInventoryCache}
            calendarMap={inventoryByDay}
            clickableElementIdsToIgnore={clickableElementIdsToIgnore}
            closeCalendar={toggleCalendar}
            currentMonth={currentMonth}
            findNextAvailable={findNextAvailable}
            focusOnClose="#DropdownGroup__selector--date"
            getAriaLabel={getAriaLabel}
            numberOfMonths={!matchMedia.sm ? 1 : 2}
            onClick={setSelectedDate}
            selectedDate={selectedDateFormatted}
          />
        )}
        <ShiftInventory
          activeNotifications={activeNotifications}
          Analytics={Analytics}
          clearTimeFilter={clearTimeFilter}
          doubleBookingError={doubleBookingError}
          filterQuery={filterQuery}
          onReservationSelect={handleReservationSelect}
          isGdaUser={userIsGda}
          isVenueClosed={isVenueClosed}
          largePartyMessage={largePartyMessage}
          loadingReservations={isVenueReservationsLoading}
          maxQuantity={maxQuantity}
          notifyData={notifyData}
          platinumNight={platinumNight}
          reservations={reservations}
          selectedDate={selectedDate}
          selectedDateFormatted={selectedDateFormatted}
          selectedQuantity={selectedQuantity}
          eventsExistOnDay={eventsExistOnDay}
          venue={venue}
          shopSmallValue={shopSmallValue}
        />
        {hasTodayEvents
          && todayEvents.map((event, idx) => (
            <Fragment key={event.url_slug}>
              <DayOfEventCard
                event={event}
                index={idx}
                lastOfType={idx === todayEventsCount - 1}
                matchMedia={matchMedia}
                onBookEvent={handleBookEvent}
                onTodayEventClick={handleTodayEventClick}
                selectedDateFormatted={selectedDateFormatted}
              />
            </Fragment>
          ))}

        {isResyCreditBannerEnabled && isResyCreditEligible && <ResyCreditBanner />}

        {hasAboutVenue && (
          <div className="about-venue">
            <h3>{aboutVenueHeader}</h3>
            <ClampLines
              className="content"
              text={aboutVenue.body || ''}
              id="venue-about"
              lines={3}
            />
          </div>
        )}

        <VenueAnnouncement
          Analytics={Analytics}
          announcement={venue.announcement}
          isWidget
          venueName={venue.name}
        />
        {hasActiveSafetyCategories && <hr />}
        {(hasActiveSafetyCategories && !isSafetyMeasuresExpanded) && (
          <SafetyMeasures
            isMobile={!matchMedia.md}
            categories={safetyCategories}
            onViewAllClick={expandSafetyMeasures}
            venueName={venue.name}
            referrer="widget"
            url={venue.safetyInfoUrl}
          />
        )}
        {(hasActiveSafetyCategories && isSafetyMeasuresExpanded) && (
          <SafetyMeasureList
            categories={safetyCategories}
            smallFont
            venueName={venue.name}
            referrer="widget"
            url={venue.safetyInfoUrl}
          />
        )}
        <UpcomingEvents
          appContext="widget"
          className="upcoming-events"
          isMobile={!matchMedia.sm}
          upcomingEvents={upcomingEvents}
          venue={venue}
        />
        <VenueLocationSummary
          Analytics={Analytics}
          announcement={venue.announcement}
          isMobile={!matchMedia.sm}
          location={venue.location}
          menuUrl={venue.contact?.menuUrl}
          name={venue.name}
          phone={venue.contact?.phone}
          social={venue.social}
          url={venue.contact?.url}
          venue={venue}
        />
        {hasAffiliatedRestaurants && (
          <AffiliatedRestaurants
            isWidget
            venues={affiliatedRestaurants}
          />
        )}
      </div>
      <PoweredBy hideCopy={hasPartnershipBranding} />
      {hasPartnershipBranding && (
        <PartnershipBranding
          alt={venue.partnershipBranding.name}
          image={venue.partnershipBranding.logo}
        />
      )}
      {displayGatingErrorModal && (
        <GatingMessageModal
          Analytics={Analytics}
          closeModal={closeModal}
          ResyAmexConfig={ResyAmexConfig}
        />
      )}
    </div>
  );
};

VenueDetailsView.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  aboutVenue: PropTypes.shape({ body: PropTypes.string }),
  aboutVenueHeader: PropTypes.string.isRequired,
  activeNotifications: PropTypes.shape({}),
  affiliatedRestaurants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isCalendarOpen: PropTypes.bool,
  clearTimeFilter: PropTypes.func.isRequired,
  clickableElementIdsToIgnore: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentMonth: momentPropTypes.momentObj.isRequired,
  dateLabel: PropTypes.string.isRequired,
  doubleBookingError: PropTypes.string,
  eventsExistOnDay: PropTypes.bool.isRequired,
  expandSafetyMeasures: PropTypes.func.isRequired,
  filterQuery: PropTypes.shape({
    day: PropTypes.string,
    party_size: PropTypes.number,
    time_filter: PropTypes.string,
  }),
  findNextAvailable: PropTypes.func.isRequired,
  focusTrap: PropTypes.shape({}).isRequired,
  getAriaLabel: PropTypes.func.isRequired,
  handleBookEvent: PropTypes.func.isRequired,
  handleReservationSelect: PropTypes.func,
  handleTodayEventClick: PropTypes.func.isRequired,
  hasAboutVenue: PropTypes.bool.isRequired,
  hasActiveSafetyCategories: PropTypes.bool.isRequired,
  hasAffiliatedRestaurants: PropTypes.bool.isRequired,
  hasNeedToKnow: PropTypes.bool.isRequired,
  hasPartnershipBranding: PropTypes.bool.isRequired,
  hasTodayEvents: PropTypes.bool.isRequired,
  inventoryByDay: PropTypes.shape({}).isRequired,
  isResyCreditEligible: PropTypes.bool,
  isVenueClosed: PropTypes.bool,
  largePartyMessage: PropTypes.string,
  loadReservations: PropTypes.func.isRequired,
  isVenueReservationsLoading: PropTypes.bool,
  matchMedia: PropTypes.shape({
    sm: PropTypes.bool,
    md: PropTypes.bool,
  }).isRequired,
  maxQuantity: PropTypes.number.isRequired,
  minQuantity: PropTypes.number.isRequired,
  needToKnow: PropTypes.shape({ body: PropTypes.string }),
  needToKnowHeader: PropTypes.string.isRequired,
  notifyData: PropTypes.shape({}),
  openNotify: PropTypes.func.isRequired,
  onPartySizeChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  platinumNight: PropTypes.shape({}),
  reservations: ReservationShape,
  safetyCategories: PropTypes.arrayOf(PropTypes.shape({})),
  isSafetyMeasuresExpanded: PropTypes.bool,
  scrollTop: PropTypes.number.isRequired,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  selectedDateFormatted: PropTypes.string.isRequired,
  selectedISODate: PropTypes.string.isRequired,
  selectedQuantity: PropTypes.number.isRequired,
  selectedTime: PropTypes.string.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  serviceTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  shopSmallValue: PropTypes.string,
  todayEvents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  todayEventsCount: PropTypes.number.isRequired,
  toggleCalendar: PropTypes.func.isRequired,
  upcomingEvents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userIsGda: PropTypes.bool.isRequired,
  venue: PropTypes.shape({
    announcement: PropTypes.shape({}),
    contact: {
      phone: PropTypes.string,
      url: PropTypes.string,
    },
    customAffiliation: PropTypes.shape({
      id: PropTypes.string,
    }),
    customAffiliations: PropTypes.arrayOf(PropTypes.shape({})),
    fb_pixel: PropTypes.string,
    featureRecaptcha: PropTypes.bool,
    id: PropTypes.number,
    largePartyMessage: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
      coords: PropTypes.shape({}),
    }),
    name: PropTypes.string,
    partnershipBranding: PropTypes.shape({
      logo: PropTypes.string,
      name: PropTypes.string,
    }),
    safetyCategories: PropTypes.arrayOf(PropTypes.shape({})),
    safetyInfoUrl: PropTypes.string,
    social: PropTypes.string,
    venueGroup: PropTypes.shape({}),
  }).isRequired,
  venueInventoryCache: PropTypes.shape({}).isRequired,
  venueId: PropTypes.number.isRequired,
  venueName: PropTypes.string.isRequired,
  timeOptions: PropTypes.shape({}),
  displayGatingErrorModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
};

VenueDetailsView.defaultProps = {
  aboutVenue: {},
  activeNotifications: {},
  doubleBookingError: undefined,
  filterQuery: {},
  handleReservationSelect: () => { },
  isCalendarOpen: false,
  isResyCreditEligible: false,
  isSafetyMeasuresExpanded: false,
  isVenueClosed: false,
  isVenueReservationsLoading: false,
  largePartyMessage: null,
  needToKnow: {},
  notifyData: {},
  platinumNight: null,
  safetyCategories: [],
  serviceTypeId: false,
  shopSmallValue: undefined,
  reservations: [],
  timeOptions: null,
};

export default VenueDetailsView;
